import InsertChartOutlinedRoundedIcon from "@mui/icons-material/InsertChartOutlinedRounded";
import { Box } from "@mui/material";
import * as React from "react";
import { DropResult, ResponderProvided } from "react-beautiful-dnd";
import { Base } from "ui/Scripts/source/framework/base";
import { EnumHelper, FrontPagePanelType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { IPagePanel, PagePanel } from "source/models/common/pagePanel";
import { IFrontPage } from "source/models/front/frontPage";
import * as frontService from "source/models/services/frontService";
import { recordLockManager } from "source/models/services/recordLockService";
import { IAppSessionState } from "source/models/store/storeTypes";
import { DashboardPanelList } from "source/components/app/DashboardPanelList";
import { HeaderMenuItem, NavigationMenu } from "source/components/app/NavigationMenu";
import { EventChart, EventChartPropertiesDialog } from "source/components/event/EventChart";
import { DashboardContainer } from "source/components/framework/Dashboard/DashboardContainer";
import { PageContainer } from "source/components/framework/Page/PageContainer";
import { PageContent } from "source/components/framework/Page/PageContent";
import { PageTitle } from "source/components/framework/Page/PageTitle";
import { PageTopBar } from "source/components/framework/Page/PageTopBar";
import { PanelContainer } from "source/components/framework/Panel/PanelContainer";
import { MessageShortList } from "source/components/message/MessageShortList/MessageShortList";
import { FrontPanelPropertiesDialog } from "../FrontPanelPropertiesDialog";
import { FrontToDoItems } from "../FrontToDoItems";
import { ModifiedRecordList } from "../ModifiedRecordList";
import { FrontBanner, FrontBannerPropertiesDialog } from "../FrontBanner";
import { FrontImagePropertiesDialog, FrontImages } from "../FrontImages";
import { UserMenu } from "source/components/app/UserMenu";
import { abortManager } from "source/models/services/tokenService";
import { ModifiedRecordPropertiesDialog } from "../ModifiedRecordList/ModifiedRecordPropertiesDialog";
import { FrontPanelMessagePropertiesDialog } from "../FrontPanelMessagePropertiesDialog";
import { AppButton } from "ui/Scripts/source/components/framework/Button/AppButton";
import { FrontDocumentBrowser } from "../FrontDocumentBrowser";
import { IFrontEventState } from "source/models/store/front/storeFront";
import { FrontTaskChart } from "../FrontTaskChart";
import { FrontTaskPanel } from "../FrontTaskPanel";

/* eslint-disable no-unused-vars */
const enum FrontPagePropertiesDialogType {
    None = "",
    General = "1",
    Image = "2",
    Banner = "3",
    EventChart = "4",
    ModifiedRecordsUser = "5",
    ModifiedRecordsOther = "6",
    Messages = "7"
}
/* eslint-enable no-unused-vars */

// Front
export interface IFrontOwnProps {
}

export interface IFrontStateProps {
    appState: IAppSessionState;
    dataId: number;
    frontPage: IFrontPage;
    event: IFrontEventState;
}

export interface IFrontDispatchProps {
    loadFront: () => void;
    onLogout: () => void;
    onSetCulture: (culture: string)=> void;
    onRefreshSessionState: () => void;
}

type FrontProp = IFrontOwnProps & IFrontStateProps & IFrontDispatchProps;

interface IFrontState {
    showNavPanel: boolean;
    panels: IPagePanel[];
    frontPanelPropertiesDialogTitle: string;
    frontPanelPropertiesDialogPanelId: string;
    frontPanelPropertiesDialogPanelType: number;
    frontPanelPropertiesDialogPanelSize: number;
    frontPanelPropertiesDialogPanelVisible: boolean;
    frontPagePropertiesDialogType: FrontPagePropertiesDialogType;
}

export class Front extends React.Component<FrontProp, IFrontState> {
    constructor(props: FrontProp) {
        super(props);
        abortManager.abortAll(); //Abort all abortable calls regardless of module
        this.state = {
            showNavPanel: false,
            panels: props.frontPage ? props.frontPage.panels.slice(0) : [],
            frontPanelPropertiesDialogTitle: "",
            frontPanelPropertiesDialogPanelId: "",
            frontPanelPropertiesDialogPanelType: 0,
            frontPanelPropertiesDialogPanelSize: 1,
            frontPanelPropertiesDialogPanelVisible: false,
            frontPagePropertiesDialogType: FrontPagePropertiesDialogType.None
        };
    }

    componentDidMount(): void {
        const sessionState = this.props.appState.state;
        recordLockManager.unlockAll(sessionState?.companyId, sessionState?.code);
        this.props.loadFront();
    }

    componentDidUpdate(prevProps: FrontProp, prevState: IFrontState): void {
        const props = this.props;
        const prevPanels = prevProps.frontPage ? prevProps.frontPage.panels.slice(0) : [];
        const panels = props.frontPage ? props.frontPage.panels.slice(0) : [];
        if (Base.equalStrArrays(prevPanels.map(i => JSON.stringify(i)), panels.map(i => JSON.stringify(i)))) return;
        this.setState({ panels });
    }

    handleToggleShowNavPanel = () => {
        this.setState({ showNavPanel: !this.state.showNavPanel });
    };

    static grid = 8;

    getPanelOptionItemStyle = (isDragging: boolean, draggableStyle: any) => {
        return {
            // some basic styles to make the items look a bit nicer
            userSelect: "none",
            // change background colour if dragging
            //background: isDragging ? "lightgreen" : "grey",
            // styles we need to apply on draggables
            ...draggableStyle
        };
    };

    getPanelOptionItemsStyle = (isDraggingOver: boolean) => {
        return {
            //  background: isDraggingOver ? "lightblue" : "lightgrey",
            //  padding: Front.grid,
            //  width: 250
        };
    };

    saveFrontPanels = async (panels: IPagePanel[]) => {
        await frontService.saveFrontPanels(panels);
        this.setState({
            panels,
            frontPagePropertiesDialogType: FrontPagePropertiesDialogType.None
        });
    };

    handleSavePanelSize = (id: string, panelType: number, panelSize: number, visible: boolean) => {
        const panels = this.state.panels.slice(0);
        const panel = panels.find(i => i.id === id && Base.equalInt(i.panelType, panelType));
        if (!panel) return;
        panel.size = panelSize;
        panel.visible = visible;
        this.saveFrontPanels(panels);
    };

    handlePanelCheckBoxDragEnd = (result: DropResult, provided: ResponderProvided) => {
        // dropped outside the list
        if (!result.destination) {
            return;
        }
        const panels = PagePanel.swapPagePanels(this.state.panels, result.source.index, result.destination.index);
        for (let i = 0; i < panels.length; i++) {
            panels[i].position = i;
        }
        this.setState({ panels });
        this.saveFrontPanels(panels);
    };

    changePanelVisibility = (panelType: number, visible: boolean) => {
        const state = this.state;
        const panels = state.panels.slice(0);
        const panel = panels.find(i => Base.equalInt(i.panelType, panelType));
        if (!panel) return;
        panel.visible = visible;
        this.saveFrontPanels(panels);
    };

    handlePanelPropertyClick = (id: string, panelType: number, panelSize: number, visible: boolean, title: string) => {
        let frontPagePropertiesDialogType = FrontPagePropertiesDialogType.General;
        if (EnumHelper.isEqual(panelType, FrontPagePanelType.Image) && this.props.appState.state.isCompanyAdmin) {
            frontPagePropertiesDialogType = FrontPagePropertiesDialogType.Image;
        } else if (EnumHelper.isEqual(panelType, FrontPagePanelType.Banner) && this.props.appState.state.isSuperAdmin) {
            frontPagePropertiesDialogType = FrontPagePropertiesDialogType.Banner;
        } else if (EnumHelper.isEqual(panelType, FrontPagePanelType.Event)) {
            frontPagePropertiesDialogType = FrontPagePropertiesDialogType.EventChart;
        } else if (EnumHelper.isEqual(panelType, FrontPagePanelType.UserModifiedRecords)) {
            frontPagePropertiesDialogType = FrontPagePropertiesDialogType.ModifiedRecordsUser;
        } else if (EnumHelper.isEqual(panelType, FrontPagePanelType.Message)) {
            frontPagePropertiesDialogType = FrontPagePropertiesDialogType.Messages;
        } else if (EnumHelper.isEqual(panelType, FrontPagePanelType.OtherModifiedRecords)) {
            frontPagePropertiesDialogType = FrontPagePropertiesDialogType.ModifiedRecordsOther;
        }
        this.setState({
            frontPagePropertiesDialogType,
            frontPanelPropertiesDialogPanelId: id,
            frontPanelPropertiesDialogPanelType: panelType,
            frontPanelPropertiesDialogPanelSize: panelSize,
            frontPanelPropertiesDialogPanelVisible: visible,
            frontPanelPropertiesDialogTitle: title
        });
    };

    handleFrontPanelPropertiesDialogCancel = () => {
        this.setState({
            frontPagePropertiesDialogType: FrontPagePropertiesDialogType.None
        });
    };

    getTitleSuffix = (dateRange: number): string => {
        return String.format(
            " (" + Translations.ForLastParameterDays + ")",
            dateRange === 0
                ? 30
                : (dateRange === 1
                    ? 60
                    : (dateRange === 2
                        ? 90
                        : 120))
        );
    };

    render() {
        const props = this.props;
        const state = this.state;
        const sessionState = props.appState.state;
        return (
            <PageContainer>
                <NavigationMenu
                    appState={props.appState}
                    activeMenuItem={HeaderMenuItem.Front}
                    activeCompanyId={sessionState.companyId}
                />
                <PageContent>
                    <PageTopBar>
                        <UserMenu
                            appState={props.appState}
                            onLogout={props.onLogout}
                            onSetCulture={props.onSetCulture}
                            onRefreshSessionState={props.onRefreshSessionState}
                        />
                    </PageTopBar>
                    <DashboardPanelList
                        companyId={sessionState.companyId}
                        hidden={!state.showNavPanel}
                        panels={state.panels}
                        isFrontPage
                        onUpdatePanels={this.saveFrontPanels}
                        onSavePanels={this.saveFrontPanels}
                        onToggleMenuPanel={this.handleToggleShowNavPanel}
                        onFrontPagePanelProperyClick={this.handlePanelPropertyClick}
                    />
                    <Box display="flex" flexDirection="column" sx={{ margin: "0 1rem" }}>
                        <PageTitle title={Translations.FrontPage + ": " + sessionState.companyName}>
                            <AppButton variant="contained" startIcon={<InsertChartOutlinedRoundedIcon />} onClick={this.handleToggleShowNavPanel}>
                                {Translations.Panels}
                            </AppButton>
                        </PageTitle>
                        <DashboardContainer>
                            {state.panels.map(panel => {
                                if (EnumHelper.isEqual(panel.panelType, FrontPagePanelType.ToDo) && panel.visible) {
                                    return <PanelContainer key={panel.getUniqueId()} size={panel.size}>
                                        <FrontToDoItems
                                            sessionState={sessionState}
                                            disableGlobalLoading={true}
                                        />
                                    </PanelContainer>;
                                } else if (EnumHelper.isEqual(panel.panelType, FrontPagePanelType.Message) && panel.visible) {
                                    return <PanelContainer key={panel.getUniqueId()} size={panel.size}>
                                        <MessageShortList
                                            sessionState={sessionState}
                                            companyId={null}
                                            disableGlobalLoading={true}
                                            frontPageMessages={props.frontPage ? props.frontPage.messages : null}
                                            getTitleSuffix={this.getTitleSuffix}
                                        />
                                    </PanelContainer>;
                                } else if (EnumHelper.isEqual(panel.panelType, FrontPagePanelType.UserModifiedRecords) && panel.visible) {
                                    return <PanelContainer key={panel.getUniqueId()} size={panel.size}>
                                        <ModifiedRecordList
                                            disableGlobalLoading={true}
                                            userModified={true}
                                            records={props.frontPage ? props.frontPage.records : null}
                                            getTitleSuffix={this.getTitleSuffix}
                                        />
                                    </PanelContainer>;
                                } else if (EnumHelper.isEqual(panel.panelType, FrontPagePanelType.OtherModifiedRecords) && panel.visible) {
                                    return <PanelContainer key={panel.getUniqueId()} size={panel.size}>
                                        <ModifiedRecordList
                                            disableGlobalLoading={true}
                                            userModified={false}
                                            records={props.frontPage ? props.frontPage.records : null}
                                            getTitleSuffix={this.getTitleSuffix}
                                        />
                                    </PanelContainer>;
                                } else if (EnumHelper.isEqual(panel.panelType, FrontPagePanelType.Event) && panel.visible) {
                                    return <PanelContainer key={panel.getUniqueId()} size={panel.size}>
                                        <EventChart
                                            companyId={null}
                                            disableGlobalLoading={true}
                                            panelPosition={panel.position}
                                            panelSize={panel.size}
                                            panelVisible={panel.visible}
                                            chartOptions={props.event ? props.event.chartOptions : null}
                                            onSavePanelSize={(panelSize: number, panelVisible: boolean) => this.handleSavePanelSize(panel.id, panel.panelType, panelSize, panelVisible)}
                                        />
                                    </PanelContainer>;
                                } else if (EnumHelper.isEqual(panel.panelType, FrontPagePanelType.Image) && panel.visible) {
                                    return <PanelContainer key={panel.getUniqueId()} size={panel.size}>
                                        <FrontImages
                                            panelSize={panel.size}
                                            panelVisible={panel.visible}
                                            images={props.frontPage ? props.frontPage.images : null}
                                            onSavePanelSize={(panelSize: number, panelVisible: boolean) => this.handleSavePanelSize(panel.id, panel.panelType, panelSize, panelVisible)}
                                        />
                                    </PanelContainer>;
                                } else if (EnumHelper.isEqual(panel.panelType, FrontPagePanelType.DocumentBrowser) && panel.visible) {
                                    return <PanelContainer key={panel.getUniqueId()} size={panel.size}>
                                        <FrontDocumentBrowser />
                                    </PanelContainer>;
                                } else if (EnumHelper.isEqual(panel.panelType, FrontPagePanelType.TaskChart) && panel.visible) {
                                    return <PanelContainer key={panel.getUniqueId()} size={panel.size}>
                                        <FrontTaskChart
                                            companyId={sessionState.companyId}
                                            id={panel.id}
                                            title={panel.title}
                                        />
                                    </PanelContainer>;
                                } else if (EnumHelper.isEqual(panel.panelType, FrontPagePanelType.TaskPanel) && panel.visible) {
                                    return <PanelContainer key={panel.getUniqueId()} size={panel.size}>
                                        <FrontTaskPanel
                                            sessionState={sessionState}
                                            companyId={sessionState.companyId}
                                            id={panel.id}
                                        />
                                    </PanelContainer>;
                                } else if (EnumHelper.isEqual(panel.panelType, FrontPagePanelType.Banner) && panel.visible) {
                                    return <PanelContainer key={panel.getUniqueId()} size={panel.size}>
                                        <FrontBanner
                                            banner={props.frontPage.banner}
                                        />
                                    </PanelContainer>;
                                } else {
                                    return null;
                                }
                            })}
                        </DashboardContainer>
                    </Box>
                </PageContent>
                {state.frontPagePropertiesDialogType === FrontPagePropertiesDialogType.General &&
                    <FrontPanelPropertiesDialog
                        title={state.frontPanelPropertiesDialogTitle}
                        id={state.frontPanelPropertiesDialogPanelId}
                        panelSize={state.frontPanelPropertiesDialogPanelSize}
                        panelType={state.frontPanelPropertiesDialogPanelType}
                        visible={state.frontPanelPropertiesDialogPanelVisible}
                        hidePanelSize={EnumHelper.isEqual(state.frontPanelPropertiesDialogPanelType, FrontPagePanelType.Banner)}
                        onOk={this.handleSavePanelSize}
                        onCancel={this.handleFrontPanelPropertiesDialogCancel}
                    />
                }
                {state.frontPagePropertiesDialogType === FrontPagePropertiesDialogType.Image &&
                    <FrontImagePropertiesDialog
                        panelSize={state.frontPanelPropertiesDialogPanelSize}
                        panelVisible={state.frontPanelPropertiesDialogPanelVisible}
                        images={props.frontPage ? props.frontPage.images.images : []}
                        imageChangeInterval={props.frontPage ? props.frontPage.images.imageChangeInterval : 0}
                        defaultImage={props.frontPage ? props.frontPage.images.defaultImage : ""}
                        onSavePanelSize={(panelSize: number, panelVisible: boolean) => this.handleSavePanelSize(state.frontPanelPropertiesDialogPanelId, state.frontPanelPropertiesDialogPanelType, panelSize, panelVisible)}
                        onClose={this.handleFrontPanelPropertiesDialogCancel}
                    />
                }
                {state.frontPagePropertiesDialogType === FrontPagePropertiesDialogType.EventChart &&
                    <EventChartPropertiesDialog
                        panelSize={state.frontPanelPropertiesDialogPanelSize}
                        panelVisible={state.frontPanelPropertiesDialogPanelVisible}
                        onSavePanelSize={(panelSize: number, panelVisible: boolean) => this.handleSavePanelSize(state.frontPanelPropertiesDialogPanelId, state.frontPanelPropertiesDialogPanelType, panelSize, panelVisible)}
                        onClose={this.handleFrontPanelPropertiesDialogCancel}
                    />
                }
                {(state.frontPagePropertiesDialogType === FrontPagePropertiesDialogType.ModifiedRecordsUser || state.frontPagePropertiesDialogType === FrontPagePropertiesDialogType.ModifiedRecordsOther) &&
                    <ModifiedRecordPropertiesDialog
                        title={state.frontPanelPropertiesDialogTitle}
                        panelSize={state.frontPanelPropertiesDialogPanelSize}
                        panelVisible={state.frontPanelPropertiesDialogPanelVisible}
                        records={props.frontPage ? props.frontPage.records : null}
                        userModified={state.frontPagePropertiesDialogType === FrontPagePropertiesDialogType.ModifiedRecordsUser}
                        onSavePanelSize={(panelSize: number, panelVisible: boolean) => this.handleSavePanelSize(state.frontPanelPropertiesDialogPanelId, state.frontPanelPropertiesDialogPanelType, panelSize, panelVisible)}
                        onClose={this.handleFrontPanelPropertiesDialogCancel}
                    />
                }
                {state.frontPagePropertiesDialogType === FrontPagePropertiesDialogType.Banner &&
                    <FrontBannerPropertiesDialog
                        panelVisible={state.frontPanelPropertiesDialogPanelVisible}
                        imageChangeInterval={props.frontPage.banner.imageChangeInterval}
                        onSavePanelSize={(panelVisible: boolean) => this.handleSavePanelSize(state.frontPanelPropertiesDialogPanelId, state.frontPanelPropertiesDialogPanelType, 1, panelVisible)}
                        onClose={this.handleFrontPanelPropertiesDialogCancel}
                    />
                }
                {state.frontPagePropertiesDialogType === FrontPagePropertiesDialogType.Messages &&
                    <FrontPanelMessagePropertiesDialog
                        title={state.frontPanelPropertiesDialogTitle}
                        panelSize={state.frontPanelPropertiesDialogPanelSize}
                        panelVisible={state.frontPanelPropertiesDialogPanelVisible}
                        messages={props.frontPage ? props.frontPage.messages : null}
                        onSavePanelSize={(panelSize: number, panelVisible: boolean) => this.handleSavePanelSize(state.frontPanelPropertiesDialogPanelId, state.frontPanelPropertiesDialogPanelType, panelSize, panelVisible)}
                        onClose={this.handleFrontPanelPropertiesDialogCancel}
                    />
                }
            </PageContainer>
        );
    }
}